import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { clientService } from '../../services/clientService';
import {
  fetchPortalAccessRequests,
  approvePortalAccessRequest,
  rejectPortalAccessRequest,
  linkClienteToRequest,
  createPortalAccess,
  resendActivation,
  createClientFromRequest,
  resetTempPassword,
  setDefinitivePassword,
} from '../../services/portalAccessRequestService';
import AdminResetPasswordModal from '../clients-management/components/AdminResetPasswordModal';

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pendiente: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
    aprobado: { label: 'Aprobado', cls: 'bg-blue-100 text-blue-800 border border-blue-200' },
    'activacion_enviada': { label: 'Activación enviada', cls: 'bg-indigo-100 text-indigo-800 border border-indigo-200' },
    activado: { label: 'Activado', cls: 'bg-green-100 text-green-800 border border-green-200' },
    rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-800 border border-red-200' },
  };
  const s = map?.[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border border-gray-200' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${s?.cls}`}>
      {s?.label}
    </span>
  );
};

// ─── Credenciales de Acceso Section ──────────────────────────────────────────
const CredencialesAcceso = ({ request, onAction, loading }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);
  const [localRequest, setLocalRequest] = useState(request);

  // Definitive password state
  const [modoDefinitiva, setModoDefinitiva] = useState(false);
  const [passwordDefinitiva, setPasswordDefinitiva] = useState('');
  const [showPasswordDefinitiva, setShowPasswordDefinitiva] = useState(false);
  const [savingDefinitiva, setSavingDefinitiva] = useState(false);

  // Sync when parent request changes
  useEffect(() => {
    setLocalRequest(request);
  }, [request]);

  const email = localRequest?.email || localRequest?.matched_client?.email;
  const tempPassword = localRequest?.temp_password;
  const passwordChangedByClient = localRequest?.password_changed_by_client;
  const hasAccess = ['activacion_enviada', 'activado']?.includes(localRequest?.status);

  // New flow fields
  const accesoCreado = localRequest?.acceso_creado === true;
  const requiereCambioPassword = localRequest?.requiere_cambio_password === true;
  const passwordTemporalActiva = localRequest?.password_temporal_activa === true;
  const accesoActivado = localRequest?.acceso_activado === true;

  const statusMap = {
    pendiente: { label: 'Sin acceso', color: 'text-gray-500', bg: 'bg-gray-100', icon: 'ShieldOff' },
    aprobado: { label: 'Acceso creado', color: 'text-blue-600', bg: 'bg-blue-50', icon: 'Shield' },
    activacion_enviada: { label: 'Activación enviada', color: 'text-indigo-600', bg: 'bg-indigo-50', icon: 'Send' },
    activado: { label: 'Portal activo', color: 'text-green-600', bg: 'bg-green-50', icon: 'ShieldCheck' },
    rechazado: { label: 'Sin acceso', color: 'text-red-500', bg: 'bg-red-50', icon: 'ShieldOff' },
  };
  const statusInfo = statusMap?.[localRequest?.status] || statusMap?.pendiente;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard?.writeText(text);
      setMsg({ type: 'success', text: `${label} copiado al portapapeles.` });
      setTimeout(() => setMsg(null), 2500);
    } catch {
      setMsg({ type: 'error', text: 'No se pudo copiar.' });
      setTimeout(() => setMsg(null), 2500);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
    let pwd = '';
    for (let i = 0; i < 10; i++) {
      pwd += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    setPasswordDefinitiva(pwd);
  };

  const handleGuardarDefinitiva = async () => {
    if (!passwordDefinitiva || passwordDefinitiva?.length < 6) {
      setMsg({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (!localRequest?.id) return;
    setSavingDefinitiva(true);
    setMsg(null);
    const result = await setDefinitivePassword(localRequest?.id, passwordDefinitiva);
    setSavingDefinitiva(false);
    if (result?.success) {
      setLocalRequest(prev => ({
        ...prev,
        temp_password: null,
        password_changed_by_client: true,
        acceso_creado: true,
        requiere_cambio_password: false,
        password_temporal_activa: false,
        acceso_activado: true,
        status: 'activado',
      }));
      setMsg({ type: 'success', text: '¡Contraseña definitiva guardada! El acceso está listo para uso inmediato.' });
      setModoDefinitiva(false);
      setPasswordDefinitiva('');
      onAction('reload');
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  const handleGeneratePassword = async () => {
    if (!localRequest?.id) return;
    setGenerating(true);
    setMsg(null);
    const result = await resetTempPassword(localRequest?.id);
    setGenerating(false);
    if (result?.success) {
      setLocalRequest(prev => ({
        ...prev,
        temp_password: result?.tempPassword,
        password_changed_by_client: false,
        temp_password_set_at: new Date()?.toISOString(),
        acceso_creado: true,
        requiere_cambio_password: true,
        password_temporal_activa: true,
        acceso_activado: false,
      }));
      setMsg({ type: 'success', text: 'Contraseña temporal generada. El cliente deberá cambiarla al primer ingreso.' });
      onAction('reload');
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  const handleResetPassword = async () => {
    if (!localRequest?.id) return;
    setResetting(true);
    setMsg(null);
    const result = await resetTempPassword(localRequest?.id);
    setResetting(false);
    if (result?.success) {
      setLocalRequest(prev => ({
        ...prev,
        temp_password: result?.tempPassword,
        password_changed_by_client: false,
        temp_password_set_at: new Date()?.toISOString(),
        acceso_creado: true,
        requiere_cambio_password: true,
        password_temporal_activa: true,
        acceso_activado: false,
      }));
      setMsg({ type: 'success', text: 'Contraseña temporal restablecida. El cliente deberá cambiarla al primer ingreso.' });
      onAction('reload');
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  return (
    <div className="rounded-xl border border-border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="KeyRound" size={15} className="text-primary" />
          Credenciales de acceso
        </h3>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusInfo?.bg} ${statusInfo?.color}`}>
          <Icon name={statusInfo?.icon} size={12} />
          {statusInfo?.label}
        </span>
      </div>

      {/* Access status indicators */}
      {(accesoCreado || hasAccess) && (
        <div className="grid grid-cols-3 gap-2">
          <div className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center ${accesoCreado ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
            <Icon name={accesoCreado ? 'CheckCircle2' : 'Circle'} size={14} className={accesoCreado ? 'text-blue-600' : 'text-gray-400'} />
            <span className={`text-[10px] font-medium leading-tight ${accesoCreado ? 'text-blue-700' : 'text-gray-400'}`}>Acceso creado</span>
          </div>
          <div className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center ${requiereCambioPassword ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
            <Icon name={requiereCambioPassword ? 'AlertCircle' : 'Circle'} size={14} className={requiereCambioPassword ? 'text-amber-600' : 'text-gray-400'} />
            <span className={`text-[10px] font-medium leading-tight ${requiereCambioPassword ? 'text-amber-700' : 'text-gray-400'}`}>Requiere cambio</span>
          </div>
          <div className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center ${accesoActivado ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
            <Icon name={accesoActivado ? 'ShieldCheck' : 'Circle'} size={14} className={accesoActivado ? 'text-green-600' : 'text-gray-400'} />
            <span className={`text-[10px] font-medium leading-tight ${accesoActivado ? 'text-green-700' : 'text-gray-400'}`}>Acceso activado</span>
          </div>
        </div>
      )}

      {/* Usuario */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Usuario (email de acceso)</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm text-foreground font-mono select-all">
            {email || <span className="text-muted-foreground italic">Sin email registrado</span>}
          </div>
          {email && (
            <button
              onClick={() => copyToClipboard(email, 'Usuario')}
              className="p-2 rounded-lg hover:bg-muted border border-border transition-smooth text-muted-foreground hover:text-foreground"
              title="Copiar usuario"
            >
              <Icon name="Copy" size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Contraseña definitiva — panel de configuración */}
      {modoDefinitiva ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
              <Icon name="ShieldCheck" size={14} className="text-blue-600" />
              Establecer contraseña definitiva
            </p>
            <button
              onClick={() => { setModoDefinitiva(false); setPasswordDefinitiva(''); setMsg(null); }}
              className="text-blue-500 hover:text-blue-700 text-xs font-medium"
            >
              Cancelar
            </button>
          </div>
          <p className="text-xs text-blue-700">
            Esta contraseña se guardará directamente en Auth. El cliente podrá usarla de inmediato sin necesidad de cambiarla.
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showPasswordDefinitiva ? 'text' : 'password'}
                value={passwordDefinitiva}
                onChange={(e) => setPasswordDefinitiva(e?.target?.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full pl-3 pr-10 py-2 border border-blue-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPasswordDefinitiva(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-600"
              >
                <Icon name={showPasswordDefinitiva ? 'EyeOff' : 'Eye'} size={14} />
              </button>
            </div>
            <button
              type="button"
              onClick={generateRandomPassword}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white text-blue-700 hover:bg-blue-100 border border-blue-300 transition-smooth whitespace-nowrap"
              title="Generar contraseña aleatoria"
            >
              <Icon name="Shuffle" size={13} />
              Generar
            </button>
          </div>
          {passwordDefinitiva && (
            <div className="flex items-center gap-1.5 text-xs">
              {passwordDefinitiva?.length >= 6
                ? <span className="text-green-600 flex items-center gap-1"><Icon name="CheckCircle2" size={12} /> Longitud válida ({passwordDefinitiva?.length} caracteres)</span>
                : <span className="text-red-500 flex items-center gap-1"><Icon name="AlertCircle" size={12} /> Mínimo 6 caracteres</span>}
            </div>
          )}
          <button
            onClick={handleGuardarDefinitiva}
            disabled={savingDefinitiva || !passwordDefinitiva || passwordDefinitiva?.length < 6}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-smooth disabled:opacity-50"
          >
            {savingDefinitiva ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="ShieldCheck" size={15} />}
            {savingDefinitiva ? 'Guardando...' : 'Guardar acceso definitivo'}
          </button>
        </div>
      ) : (
        <>
          {/* Contraseña temporal */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Contraseña temporal</label>
            {passwordChangedByClient ? (
              /* Client already changed password — show info + reset option */
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                <Icon name="CheckCircle2" size={15} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-700">El cliente ya cambió su contraseña</p>
                  <p className="text-xs text-green-600 mt-0.5">La contraseña temporal ya no está disponible. Puedes restablecer una nueva si el cliente lo necesita.</p>
                </div>
              </div>
            ) : tempPassword ? (
              /* Temp password exists — show masked field with eye + copy */
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono select-all tracking-wider">
                    {showPassword ? tempPassword : '•'?.repeat(Math.min(tempPassword?.length || 12, 16))}
                  </div>
                  <button
                    onClick={() => setShowPassword(v => !v)}
                    className="p-2 rounded-lg hover:bg-muted border border-border transition-smooth text-muted-foreground hover:text-foreground"
                    title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={15} />
                  </button>
                  <button
                    onClick={() => copyToClipboard(tempPassword, 'Contraseña temporal')}
                    className="p-2 rounded-lg hover:bg-muted border border-border transition-smooth text-muted-foreground hover:text-foreground"
                    title="Copiar contraseña temporal"
                  >
                    <Icon name="Copy" size={15} />
                  </button>
                </div>
                {localRequest?.temp_password_set_at && (
                  <p className="text-xs text-muted-foreground">
                    Generada: {new Date(localRequest.temp_password_set_at)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
                {requiereCambioPassword && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Icon name="AlertCircle" size={12} className="text-amber-500" />
                    <span className="text-xs text-amber-600">El cliente debe cambiar esta contraseña al primer ingreso</span>
                  </div>
                )}
              </div>
            ) : (
              /* No temp password yet */
              <div className="px-3 py-2 bg-muted rounded-lg text-sm text-muted-foreground italic">
                Sin contraseña temporal activa
              </div>
            )}
          </div>

          {/* Action buttons */}
          {!passwordChangedByClient && !tempPassword && (
            /* No password at all — show Generate button */
            <button
              onClick={handleGeneratePassword}
              disabled={generating || loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-smooth disabled:opacity-50"
            >
              {generating ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="KeyRound" size={15} />}
              Generar contraseña temporal
            </button>
          )}

          {(tempPassword || passwordChangedByClient || hasAccess) && (
            /* Password exists or access exists — show Reset button */
            <button
              onClick={handleResetPassword}
              disabled={resetting || loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 transition-smooth disabled:opacity-50"
            >
              {resetting ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="RefreshCw" size={15} />}
              Restablecer contraseña temporal
            </button>
          )}
        </>
      )}

      {/* Definitive password toggle button — always visible when email exists */}
      {email && !modoDefinitiva && (
        <button
          onClick={() => { setModoDefinitiva(true); setMsg(null); }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-smooth"
        >
          <Icon name="ShieldCheck" size={15} />
          Establecer contraseña definitiva
        </button>
      )}

      {msg && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${msg?.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          <Icon name={msg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={14} />
          {msg?.text}
        </div>
      )}
    </div>
  );
};

// ─── Vincular Cliente Modal ───────────────────────────────────────────────────
const VincularClienteModal = ({ request, onClose, onLinked, companyId }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSearch = useCallback(async () => {
    if (!search?.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('clients')?.select('id, nombre, email, telefono, company_id')?.eq('company_id', companyId)?.or(`nombre.ilike.%${search}%,email.ilike.%${search}%,telefono.ilike.%${search}%`)?.limit(10);
      if (error) {
        setMsg({ type: 'error', text: `Error al buscar: ${error?.message}` });
      } else {
        setResults(data || []);
        if (!data?.length) setMsg({ type: 'info', text: 'No se encontraron clientes.' });
        else setMsg(null);
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Error inesperado al buscar.' });
    } finally {
      setLoading(false);
    }
  }, [search, companyId]);

  const handleLink = async (cliente) => {
    setLinking(true);
    setMsg(null);
    const result = await linkClienteToRequest(request?.id, cliente?.id, cliente?.company_id);
    setLinking(false);
    if (result?.success) {
      setMsg({ type: 'success', text: `Vinculado a ${cliente?.nombre} correctamente.` });
      setTimeout(() => { onLinked(); onClose(); }, 1200);
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-foreground">Vincular a Cliente Existente</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
            Solicitud de: <span className="font-medium text-foreground">{request?.nombre}</span>
            {request?.email && <> · {request?.email}</>}
            {request?.telefono && <> · {request?.telefono}</>}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por nombre, email o teléfono..."
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={loading || !search?.trim()} size="sm">
              {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Search" size={16} />}
            </Button>
          </div>
          {msg && (
            <div className={`p-3 rounded-lg text-sm ${msg?.type === 'success' ? 'bg-green-50 text-green-700' : msg?.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
              {msg?.text}
            </div>
          )}
          {results?.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {results?.map((c) => (
                <div key={c?.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted transition-smooth">
                  <div>
                    <p className="font-medium text-sm text-foreground">{c?.nombre}</p>
                    <p className="text-xs text-muted-foreground">{c?.email || '—'} · {c?.telefono || '—'}</p>
                  </div>
                  <Button size="sm" onClick={() => handleLink(c)} disabled={linking}>Vincular</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Crear Cliente Modal ──────────────────────────────────────────────────────
const CrearClienteModal = ({ request, onClose, onCreated, companyId }) => {
  const [form, setForm] = useState({
    nombre: request?.nombre || '',telefono: request?.telefono || '',email: request?.email || '',
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleCreate = async () => {
    if (!form?.nombre?.trim()) {
      setMsg({ type: 'error', text: 'El nombre es requerido.' });
      return;
    }
    setLoading(true);
    setMsg(null);
    const result = await createClientFromRequest(request?.id, {
      nombre: form?.nombre?.trim(),
      telefono: form?.telefono?.trim() || null,
      email: form?.email?.trim()?.toLowerCase() || null,
      company_id: companyId,
    });
    setLoading(false);
    if (result?.success) {
      setMsg({ type: 'success', text: `Cliente "${form?.nombre}" creado y vinculado correctamente.` });
      setTimeout(() => { onCreated(); onClose(); }, 1400);
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-foreground">Crear Nuevo Cliente</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">Se creará un nuevo cliente con los datos de la solicitud y se vinculará automáticamente.</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Nombre *</label>
              <Input value={form?.nombre} onChange={(e) => setForm(f => ({ ...f, nombre: e?.target?.value }))} placeholder="Nombre completo" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Teléfono</label>
              <Input value={form?.telefono} onChange={(e) => setForm(f => ({ ...f, telefono: e?.target?.value }))} placeholder="Teléfono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
              <Input value={form?.email} onChange={(e) => setForm(f => ({ ...f, email: e?.target?.value }))} placeholder="correo@ejemplo.com" />
            </div>
          </div>
          {msg && (
            <div className={`p-3 rounded-lg text-sm ${msg?.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {msg?.text}
            </div>
          )}
        </div>
        <div className="flex gap-2 p-5 border-t border-border">
          <Button onClick={handleCreate} disabled={loading} className="flex-1">
            {loading ? <Icon name="Loader2" size={16} className="animate-spin mr-2" /> : <Icon name="UserPlus" size={16} className="mr-2" />}
            Crear cliente
          </Button>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const DetailModal = ({ request, onClose, onAction, userId, companyId, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showVincular, setShowVincular] = useState(false);
  const [showCrearCliente, setShowCrearCliente] = useState(false);
  const [matchedUnits, setMatchedUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [fullClientData, setFullClientData] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const matchedClient = request?.matched_client;

  // Load full client data (including vehicles) using clientService when modal opens
  useEffect(() => {
    const loadClientData = async () => {
      if (!matchedClient?.id) {
        setMatchedUnits([]);
        setFullClientData(null);
        return;
      }
      setUnitsLoading(true);
      try {
        const clientData = await clientService?.getById(matchedClient?.id);
        if (clientData) {
          setFullClientData(clientData);
          // vehicles are nested as clientData.vehicles (camelCase from toCamelCase)
          const vehicles = clientData?.vehicles || [];
          setMatchedUnits(vehicles);
        } else {
          // Fallback: direct query using the correct FK column
          const { data } = await supabase?.from('vehicles')?.select('id, marca, modelo, anio, placas, color, vin, tipo')?.eq('client_id', matchedClient?.id)?.limit(20);
          setMatchedUnits(data || []);
        }
      } catch (err) {
        console.warn('[DetailModal] client data load error:', err?.message);
        // Fallback: direct query
        try {
          const { data } = await supabase?.from('vehicles')?.select('id, marca, modelo, anio, placas, color, vin, tipo')?.eq('client_id', matchedClient?.id)?.limit(20);
          setMatchedUnits(data || []);
        } catch (fallbackErr) {
          console.warn('[DetailModal] fallback vehicles query error:', fallbackErr?.message);
        }
      } finally {
        setUnitsLoading(false);
      }
    };
    loadClientData();
  }, [matchedClient?.id]);

  const handleApprove = async () => {
    setLoading(true);
    setMsg(null);
    const result = await approvePortalAccessRequest(request?.id, userId);
    setLoading(false);
    if (result?.success) {
      setMsg({ type: 'success', text: 'Solicitud aprobada correctamente.' });
      setTimeout(() => { onAction('reload'); onClose(); }, 1200);
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  const handleReject = async () => {
    setLoading(true);
    setMsg(null);
    const result = await rejectPortalAccessRequest(request?.id, userId);
    setLoading(false);
    if (result?.success) {
      setMsg({ type: 'success', text: 'Solicitud rechazada.' });
      setTimeout(() => { onAction('reload'); onClose(); }, 1200);
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  const handleCrearAcceso = async () => {
    const email = request?.email || matchedClient?.email;
    if (!email) {
      setMsg({ type: 'error', text: 'Se requiere correo electrónico para crear acceso portal.' });
      return;
    }
    if (!matchedClient?.id) {
      setMsg({ type: 'error', text: 'Primero vincula o crea un cliente para esta solicitud.' });
      return;
    }
    setLoading(true);
    setMsg(null);
    const result = await createPortalAccess(request?.id, matchedClient?.id, email, userId);
    setLoading(false);
    if (result?.success) {
      setMsg({ type: 'success', text: 'Acceso portal creado. Enlace de activación enviado.' });
      setTimeout(() => { onAction('reload'); onClose(); }, 1500);
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  const handleResend = async () => {
    const email = request?.email || matchedClient?.email;
    const telefono = request?.telefono || matchedClient?.telefono;
    if (!email && !telefono) {
      setMsg({ type: 'error', text: 'No hay email ni teléfono para reenviar activación.' });
      return;
    }
    setLoading(true);
    setMsg(null);
    const result = await resendActivation(request?.id, email, telefono);
    setLoading(false);
    if (result?.success) {
      const channels = [];
      if (email) channels?.push('email');
      if (telefono) channels?.push('WhatsApp');
      setMsg({ type: 'success', text: `Activación reenviada por ${channels?.join(' y ')}.` });
      setTimeout(() => { onAction('reload'); }, 1500);
    } else {
      setMsg({ type: 'error', text: result?.error });
    }
  };

  const canApprove = request?.status === 'pendiente';
  const canReject = request?.status === 'pendiente';
  const canCreateAccess = (request?.status === 'aprobado') && matchedClient?.id && !matchedClient?.portal_user_id;
  const canResend = ['activacion_enviada', 'activado']?.includes(request?.status);
  const canVincular = !matchedClient?.id;
  const canCrearCliente = !matchedClient?.id;

  // Roles authorized to see credentials
  const canSeeCredentials = ['admin', 'direccion', 'ventas', 'recepcion']?.includes(userRole);
  // Only admin/dirección can manually reset password
  const canResetPassword = ['admin', 'direccion']?.includes(userRole) && !!(request?.email || matchedClient?.email);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="p-2.5 bg-primary/10 rounded-xl flex-shrink-0 ml-10 lg:ml-0">
                <Icon name="ShieldCheck" size={24} className="text-primary" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-heading font-semibold text-foreground">Detalle de Solicitud</h2>
                <p className="text-xs text-muted-foreground mt-1">ID: {request?.id?.slice(0, 8)}...</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-smooth">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {/* Datos de la solicitud */}
            <div className="bg-muted/50 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="User" size={15} className="text-primary" />
                Datos del solicitante
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Nombre</p>
                  <p className="text-sm font-medium text-foreground">{request?.nombre || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Teléfono</p>
                  <p className="text-sm font-medium text-foreground">{request?.telefono || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm font-medium text-foreground">{request?.email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fecha de solicitud</p>
                  <p className="text-sm font-medium text-foreground">
                    {request?.created_at
                      ? new Date(request.created_at)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Estado</p>
                  <div className="mt-0.5"><StatusBadge status={request?.status} /></div>
                </div>
                {request?.notes && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-muted-foreground">Notas</p>
                    <p className="text-sm text-foreground">{request?.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Coincidencia con cliente */}
            <div className="rounded-xl border border-border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Users" size={15} className="text-primary" />
                Coincidencia con cliente existente
              </h3>
              {matchedClient ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowClientDetail(v => !v)}
                    className="w-full text-left p-3 bg-green-50 border border-green-200 rounded-lg space-y-1 hover:bg-green-100 transition-smooth"
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="UserCheck" size={15} className="text-green-600" />
                      <p className="text-sm font-semibold text-green-700">{matchedClient?.nombre}</p>
                      {matchedClient?.portal_user_id && (
                        <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Portal activo</span>
                      )}
                      <Icon name={showClientDetail ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-green-600 ml-auto" />
                    </div>
                    <p className="text-xs text-green-700">{matchedClient?.email || '—'} · {matchedClient?.telefono || '—'}</p>
                    <p className="text-xs text-green-600 font-medium">Toca para ver ficha completa</p>
                  </button>
                  {showClientDetail && fullClientData && (
                    <div className="p-3 bg-muted/50 border border-border rounded-lg space-y-2 text-sm">
                      <p className="font-semibold text-foreground text-xs uppercase tracking-wide text-muted-foreground mb-1">Ficha del cliente</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-muted-foreground">Nombre</p>
                          <p className="text-sm font-medium text-foreground">{fullClientData?.nombre || matchedClient?.nombre || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Teléfono</p>
                          <p className="text-sm font-medium text-foreground">{fullClientData?.telefono || matchedClient?.telefono || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="text-sm font-medium text-foreground">{fullClientData?.email || matchedClient?.email || '—'}</p>
                        </div>
                        {(fullClientData?.empresa || fullClientData?.razonSocial) && (
                          <div>
                            <p className="text-xs text-muted-foreground">Empresa</p>
                            <p className="text-sm font-medium text-foreground">{fullClientData?.empresa || fullClientData?.razonSocial}</p>
                          </div>
                        )}
                        {fullClientData?.rfc && (
                          <div>
                            <p className="text-xs text-muted-foreground">RFC</p>
                            <p className="text-sm font-medium text-foreground">{fullClientData?.rfc}</p>
                          </div>
                        )}
                        {(fullClientData?.direccion || fullClientData?.address) && (
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">Dirección</p>
                            <p className="text-sm font-medium text-foreground">{fullClientData?.direccion || fullClientData?.address}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                  <Icon name="AlertTriangle" size={15} className="text-yellow-600" />
                  <p className="text-sm text-yellow-700">Sin coincidencia con cliente existente.</p>
                </div>
              )}
            </div>

            {/* Coincidencia con unidades */}
            <div className="rounded-xl border border-border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Car" size={15} className="text-primary" />
                Unidades relacionadas
                {matchedUnits?.length > 0 && (
                  <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{matchedUnits?.length}</span>
                )}
              </h3>
              {unitsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  Buscando unidades...
                </div>
              ) : matchedUnits?.length > 0 ? (
                <div className="space-y-2">
                  {matchedUnits?.map((v) => (
                    <div key={v?.id} className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                      <Icon name="Car" size={14} className="text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          {[v?.marca, v?.modelo, v?.anio]?.filter(Boolean)?.join(' ')}
                        </p>
                        <p className="text-xs text-blue-600">
                          {v?.placas ? `Placas: ${v?.placas}` : 'Sin placas'}{v?.color ? ` · ${v?.color}` : ''}{v?.vin ? ` · VIN: ${v?.vin}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {matchedClient?.id ? 'Sin unidades registradas para este cliente.' : 'Vincula un cliente para ver sus unidades.'}
                </p>
              )}
            </div>

            {/* Credenciales de acceso — solo roles autorizados */}
            {canSeeCredentials && (
              <CredencialesAcceso
                request={request}
                onAction={onAction}
                loading={loading}
              />
            )}

            {/* Mensaje de acción */}
            {msg && (
              <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${msg?.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                <Icon name={msg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={15} />
                {msg?.text}
              </div>
            )}

            {/* Acciones */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Acciones</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {canApprove && (
                  <button
                    onClick={handleApprove}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-smooth disabled:opacity-50"
                  >
                    {loading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Check" size={15} />}
                    1. Aprobar solicitud
                  </button>
                )}
                {canReject && (
                  <button
                    onClick={handleReject}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-smooth disabled:opacity-50"
                  >
                    {loading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="X" size={15} />}
                    2. Rechazar solicitud
                  </button>
                )}
                {canVincular && (
                  <button
                    onClick={() => setShowVincular(true)}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 transition-smooth disabled:opacity-50"
                  >
                    <Icon name="Link" size={15} />
                    3. Vincular cliente existente
                  </button>
                )}
                {canCrearCliente && (
                  <button
                    onClick={() => setShowCrearCliente(true)}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 transition-smooth disabled:opacity-50"
                  >
                    <Icon name="UserPlus" size={15} />
                    4. Crear nuevo cliente
                  </button>
                )}
                {canCreateAccess && (
                  <button
                    onClick={handleCrearAcceso}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-smooth disabled:opacity-50 sm:col-span-2"
                  >
                    {loading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="ShieldCheck" size={15} />}
                    6. Crear acceso portal y enviar activación
                  </button>
                )}
                {canResend && (
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-smooth disabled:opacity-50 sm:col-span-2"
                  >
                    {loading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Send" size={15} />}
                    Reenviar acceso (email / WhatsApp)
                  </button>
                )}
                {canResetPassword && (
                  <button
                    onClick={() => setShowResetModal(true)}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 transition-smooth disabled:opacity-50 sm:col-span-2"
                  >
                    <Icon name="KeyRound" size={15} />
                    Restablecer contraseña (admin)
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end p-5 border-t border-border">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          </div>
        </div>
      </div>
      {showVincular && (
        <div className="z-50">
          <VincularClienteModal
            request={request}
            companyId={companyId}
            onClose={() => setShowVincular(false)}
            onLinked={() => { setShowVincular(false); onAction('reload'); onClose(); }}
          />
        </div>
      )}
      {showCrearCliente && (
        <div className="z-50">
          <CrearClienteModal
            request={request}
            companyId={companyId}
            onClose={() => setShowCrearCliente(false)}
            onCreated={() => { setShowCrearCliente(false); onAction('reload'); onClose(); }}
          />
        </div>
      )}
      {showResetModal && (
        <AdminResetPasswordModal
          client={{
            id: matchedClient?.id || null,
            nombre: matchedClient?.nombre || request?.nombre,
            email: request?.email || matchedClient?.email,
            telefono: request?.telefono || matchedClient?.telefono,
            portal_user_id: matchedClient?.portal_user_id || null,
          }}
          portalAccessRequestId={request?.id}
          companyId={companyId}
          performedBy={{ id: userId, nombre: null }}
          onClose={() => setShowResetModal(false)}
          onSuccess={() => {
            setShowResetModal(false);
            onAction('reload');
          }}
        />
      )}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AccesosPortal = () => {
  const { userProfile, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  // Detail modal
  const [detailModal, setDetailModal] = useState(null);

  const companyId = userProfile?.company_id;

  const loadRequests = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    const result = await fetchPortalAccessRequests(companyId);
    setLoading(false);
    if (result?.success) {
      setRequests(result?.data);
    } else {
      setError(result?.error);
    }
  }, [companyId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Real-time subscription
  useEffect(() => {
    if (!companyId) return;
    const channel = supabase?.channel(`accesos_portal_${companyId}`)?.on('postgres_changes', { event: '*', schema: 'public', table: 'portal_access_requests' }, () => {
        loadRequests();
      })?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [companyId, loadRequests]);

  const showMsg = (type, text) => {
    setActionMsg({ type, text });
    setTimeout(() => setActionMsg(null), 4000);
  };

  const handleDetailAction = (action) => {
    if (action === 'reload') {
      loadRequests();
      showMsg('success', 'Acción completada correctamente.');
    }
  };

  const pendingCount = requests?.filter((r) => r?.status === 'pendiente')?.length;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <MainSidebar />
      <main className="flex-1 overflow-y-auto lg:ml-64">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="p-2.5 bg-primary/10 rounded-xl flex-shrink-0 ml-10 lg:ml-0">
                <Icon name="ShieldCheck" size={24} className="text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl lg:text-2xl font-heading font-bold text-foreground leading-tight">Accesos Portal</h1>
                <p className="text-sm text-muted-foreground mt-1">Gestión de solicitudes de acceso al Portal Cliente</p>
              </div>
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold bg-yellow-500 text-white flex-shrink-0">
                  {pendingCount}
                </span>
              )}
            </div>
            <Button onClick={loadRequests} variant="outline" size="sm" className="flex-shrink-0">
              <Icon name="RefreshCw" size={16} className="mr-2" />
              <span className="hidden sm:inline">Actualizar</span>
            </Button>
          </div>

          {/* Action message */}
          {actionMsg && (
            <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${actionMsg?.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              <Icon name={actionMsg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} />
              <span className="text-sm font-medium">{actionMsg?.text}</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Pendientes', status: 'pendiente', color: 'yellow', icon: 'Clock' },
              { label: 'Aprobados', status: 'aprobado', color: 'blue', icon: 'CheckCircle' },
              { label: 'Activación enviada', status: 'activacion_enviada', color: 'indigo', icon: 'Send' },
              { label: 'Activados', status: 'activado', color: 'green', icon: 'ShieldCheck' },
              { label: 'Rechazados', status: 'rechazado', color: 'red', icon: 'XCircle' },
            ]?.map(({ label, status, color, icon }) => {
              const count = requests?.filter((r) => r?.status === status)?.length;
              return (
                <div key={status} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={icon} size={16} className={`text-${color}-500`} />
                    <span className="text-xs text-muted-foreground">{label}</span>
                  </div>
                  <p className={`text-2xl font-bold text-${color}-600`}>{count}</p>
                </div>
              );
            })}
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Icon name="AlertCircle" size={32} className="text-destructive" />
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button onClick={loadRequests} size="sm" variant="outline">Reintentar</Button>
              </div>
            ) : requests?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Icon name="ShieldOff" size={40} className="text-muted-foreground/40" />
                <p className="text-muted-foreground font-medium">Sin solicitudes de acceso</p>
                <p className="text-sm text-muted-foreground">Las solicitudes del Portal Cliente aparecerán aquí.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nombre</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Teléfono</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fecha</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Estado</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cliente</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ver detalle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {requests?.map((req) => {
                      const matchedClient = req?.matched_client;
                      return (
                        <tr
                          key={req?.id}
                          onClick={() => setDetailModal(req)}
                          className="hover:bg-muted/40 transition-smooth cursor-pointer"
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground">{req?.nombre}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-muted-foreground">{req?.telefono || '—'}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-muted-foreground">{req?.email || '—'}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-muted-foreground">
                              {new Date(req.created_at)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                            <p className="text-xs text-muted-foreground/70">
                              {new Date(req.created_at)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={req?.status} />
                          </td>
                          <td className="px-4 py-3">
                            {matchedClient ? (
                              <div className="flex items-center gap-1.5">
                                <Icon name="UserCheck" size={14} className="text-green-500 flex-shrink-0" />
                                <span className="text-xs text-green-700 font-medium truncate max-w-[120px]" title={matchedClient?.nombre}>
                                  {matchedClient?.nombre}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground/60 italic">Sin coincidencia</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={(e) => { e?.stopPropagation(); setDetailModal(req); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-smooth"
                            >
                              <Icon name="Eye" size={13} />
                              Abrir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Detail Modal */}
      {detailModal && (
        <DetailModal
          request={detailModal}
          userId={user?.id}
          companyId={companyId}
          userRole={userProfile?.role}
          onClose={() => setDetailModal(null)}
          onAction={handleDetailAction}
        />
      )}
    </div>
  );
};

export default AccesosPortal;
